/// <reference types="google.maps" />
import { useEffect, useMemo } from 'react'
import { Paper, Text } from '@mantine/core'
import { APIProvider, AdvancedMarker, Map, Pin, useMap } from '@vis.gl/react-google-maps'
import { config } from '@/config'

/** The fields the map reads — facility discovery and referral clinics both satisfy it. */
export type MapPoint = { placeId: string; name: string; lat?: number; lon?: number }

// Places results may only be shown on a Google map (Maps Platform terms), so this
// is Google Maps or no map. Without a browser key the list works alone.
const MAP_ID = 'DEMO_MAP_ID'
// Design 1a: pins are numbered to match the list order; the selected pin is green,
// the rest navy, and hovering a row lifts its pin.
const SELECTED = '#2E9E6B'
const UNSELECTED = '#103F6B'

type LatLng = { lat: number; lng: number }

function FitBounds({
  points,
  pointsKey,
  focus,
}: {
  points: LatLng[]
  pointsKey: string
  focus: LatLng | null
}) {
  const map = useMap()
  // `points` is a fresh array every render; key on the facility positions (pointsKey)
  // so this only changes — and the fit-bounds effect below only re-runs — when the
  // facility set/positions actually change, not on every render (e.g. a pure
  // selectedPlaceId change), which would otherwise reset the zoom and fight panTo.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stablePoints = useMemo(() => points, [pointsKey])
  useEffect(() => {
    if (!map || typeof google === 'undefined') return
    const bounds = new google.maps.LatLngBounds()
    stablePoints.forEach((p) => bounds.extend(p))
    map.fitBounds(bounds, 40)
  }, [map, stablePoints])
  useEffect(() => {
    if (map && focus) map.panTo(focus)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, focus?.lat, focus?.lng])
  return null
}

export type FacilityMapProps = {
  /** In list order — pin numbers follow the array index. */
  facilities: MapPoint[]
  selectedPlaceId: string | null
  hoveredPlaceId?: string | null
  onSelect: (placeId: string) => void
  /** Absent when the patient's ZIP could not be geocoded (coordinate-free fallback). */
  patient: { lat: number; lon: number } | null
}

export const FacilityMap = ({
  facilities,
  selectedPlaceId,
  hoveredPlaceId,
  onSelect,
  patient,
}: FacilityMapProps) => {
  const apiKey = config.googleMapsBrowserKey
  if (!apiKey) {
    return (
      <Paper withBorder p="md" className="flex h-full min-h-[280px] items-center justify-center">
        <Text size="sm" c="dimmed">
          Map unavailable (no Google Maps key configured)
        </Text>
      </Paper>
    )
  }
  const patientPos: LatLng | null = patient ? { lat: patient.lat, lng: patient.lon } : null
  // Saved facilities without a geolocation have no pin; the list keeps their number so
  // the pins that do exist still match the rows.
  const located = facilities.flatMap((f, i) =>
    f.lat !== undefined && f.lon !== undefined ? [{ f, i, pos: { lat: f.lat, lng: f.lon } }] : [],
  )
  const facilityPoints = located.map((l) => l.pos)
  const points = patientPos ? [patientPos, ...facilityPoints] : facilityPoints
  // Without a patient pin, centre on the first facility (the fit-bounds effect takes
  // over once there are points); the continental-US centre is only the empty-list case.
  const defaultCenter = patientPos ?? facilityPoints[0] ?? { lat: 39.5, lng: -98.35 }
  const pointsKey = points.map((p) => `${p.lat},${p.lng}`).join('|')
  const selected = facilities.find((f) => f.placeId === selectedPlaceId)
  const focus =
    selected && selected.lat !== undefined && selected.lon !== undefined
      ? { lat: selected.lat, lng: selected.lon }
      : null

  return (
    <div className="h-full min-h-[280px] overflow-hidden rounded-md border border-gray-200">
      <APIProvider apiKey={apiKey}>
        <Map
          mapId={MAP_ID}
          defaultCenter={defaultCenter}
          defaultZoom={11}
          gestureHandling="greedy"
          disableDefaultUI={false}
          style={{ width: '100%', height: '100%' }}
        >
          <FitBounds points={points} pointsKey={pointsKey} focus={focus} />
          {patientPos && (
            <AdvancedMarker position={patientPos} title="Patient address" zIndex={1}>
              <Pin background="#111827" borderColor="#111827" glyphColor="#fff" glyph="P" />
            </AdvancedMarker>
          )}
          {located.map(({ f, i, pos }) => {
            const isSelected = f.placeId === selectedPlaceId
            const isHovered = f.placeId === hoveredPlaceId
            return (
              <AdvancedMarker
                key={f.placeId}
                position={pos}
                title={`${i + 1}. ${f.name}`}
                onClick={() => onSelect(f.placeId)}
                zIndex={isSelected ? 4 : isHovered ? 3 : 2}
              >
                <Pin
                  background={isSelected ? SELECTED : UNSELECTED}
                  borderColor={isSelected ? '#268159' : '#0B2D4D'}
                  glyphColor="#fff"
                  glyph={String(i + 1)}
                  scale={isSelected || isHovered ? 1.2 : 1}
                />
              </AdvancedMarker>
            )
          })}
        </Map>
      </APIProvider>
    </div>
  )
}
